import { useParams } from 'react-router-dom';
import { useState, useCallback } from 'react';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import { useQueryClient } from '@tanstack/react-query';
import styles from './InspectionFindingsPage.module.scss';
import LoadingSpinner from '../../components/LoadingSpinner';
import Finding from '../../components/finding/Finding';
import { useInspectionFindings } from '../../api/hooks/useInspectionFindings';
import { useInspectionPrograms } from '../../api/hooks/useInspectionProgram';
import { inspectionFindingService } from '../../api/services/inspection-finding.service';
import { inspectionFindingItemService } from '../../api/services/inspection-finding-item.service';
import { InspectionsFindingDto, InspectionsFindingsItemDto } from '../../api/types/dtos/inspection';
import { useOrganization } from '../../contexts/OrganizationContext';
import { generateFindingsCSV, downloadCSV } from '../../utils/csv';

const FINDINGS_KEY = 'inspection-findings';
const FINDING_ITEMS_KEY = 'inspection-finding-items';

export const InspectionFindingsPage: React.FC = () => {
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const [undoStack, setUndoStack] = useState<{ action: string; data: any }[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  
  const { data: findings, isLoading: findingsLoading, error: findingsError } = useInspectionFindings(
    selectedOrganization?.id || '',
    inspectionId || '',
    true
  );

  const { data: inspectionPrograms, isLoading: programsLoading, error: programsError } = useInspectionPrograms(
    selectedOrganization?.id || '',
    inspectionId || ''
  );

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  const showMessage = (message: string) => {
    setSnackbar({ open: true, message });
  };

  const invalidateQueries = useCallback(() => {
    if (!selectedOrganization?.id || !inspectionId) return;
    queryClient.invalidateQueries({
      queryKey: [FINDINGS_KEY, selectedOrganization.id, inspectionId],
    });
    queryClient.invalidateQueries({
      queryKey: [FINDING_ITEMS_KEY, selectedOrganization.id, inspectionId],
    });
  }, [selectedOrganization?.id, inspectionId, queryClient]);

  const handleUpdate = useCallback(async (finding: InspectionsFindingDto) => {
    try {
      if (!finding.id || !inspectionId || !selectedOrganization?.id) return;
      
      await inspectionFindingService.update(selectedOrganization.id, inspectionId, finding.id, finding);
      invalidateQueries();
      showMessage('Finding updated successfully');
    } catch (error) {
      showMessage('Error updating finding');
      console.error('Error updating finding:', error);
    }
  }, [inspectionId, selectedOrganization?.id, invalidateQueries]);

  const handleAddFindingItem = useCallback(async (findingId: string, item: Omit<InspectionsFindingsItemDto, 'findingId'>) => {
    try {
      if (!inspectionId || !selectedOrganization?.id) return;

      // Create new finding item with the current finding's ID
      const newItem: InspectionsFindingsItemDto = {
        item: item.item,
        findingId,
        isActive: true
      };

      const response = await inspectionFindingItemService.create(
        selectedOrganization.id,
        inspectionId,
        findingId,
        newItem
      );

      invalidateQueries();
      
      // Add to undo stack
      setUndoStack(prev => [...prev, { 
        action: 'add', 
        data: { findingId, item: response.data } 
      }]);
      
      showMessage('Criteria added successfully');
    } catch (error) {
      showMessage('Error adding criteria');
      console.error('Error adding finding item:', error);
    }
  }, [inspectionId, selectedOrganization?.id, invalidateQueries]);

  const handleRemoveFindingItem = useCallback(async (findingId: string, itemId: string) => {
    try {
      if (!inspectionId || !selectedOrganization?.id) return;

      // Store the item before deletion for undo
      const finding = findings?.find(f => f.id === findingId);
      const removedItem = finding?.inspectionsFindingsItems?.find(item => item.id === itemId);

      await inspectionFindingItemService.delete(
        selectedOrganization.id,
        inspectionId,
        findingId,
        itemId
      );

      invalidateQueries();
      
      // Add to undo stack
      if (removedItem) {
        setUndoStack(prev => [...prev, { 
          action: 'remove', 
          data: { findingId, item: removedItem } 
        }]);
      }
      
      showMessage('Criteria removed successfully');
    } catch (error) {
      showMessage('Error removing criteria');
      console.error('Error removing finding item:', error);
    }
  }, [inspectionId, selectedOrganization?.id, findings, invalidateQueries]);

  const handleUndo = useCallback(async () => {
    const lastAction = undoStack[undoStack.length - 1];
    if (!lastAction) return;

    try {
      if (lastAction.action === 'add') {
        await handleRemoveFindingItem(lastAction.data.findingId, lastAction.data.item.id);
      } else if (lastAction.action === 'remove') {
        await handleAddFindingItem(lastAction.data.findingId, lastAction.data.item);
      }

      setUndoStack(prev => prev.slice(0, -1));
      showMessage('Action undone successfully');
    } catch (error) {
      showMessage('Error undoing action');
      console.error('Error undoing action:', error);
    }
  }, [undoStack, handleAddFindingItem, handleRemoveFindingItem]);

  const handleDownloadCSV = useCallback(() => {
    if (!findings) return;

    const csvContent = generateFindingsCSV(findings);
    const date = new Date().toISOString().split('T')[0];
    const filename = `inspection_findings_${date}.csv`;
    downloadCSV(csvContent, filename);
    showMessage('CSV downloaded successfully');
  }, [findings]);

  // Error handling
  if (findingsError || programsError) {
    return (
      <Container>
        <Alert severity="error" className={styles.error}>
          {findingsError?.message || programsError?.message}
        </Alert>
      </Container>
    );
  }

  // Loading state
  if (findingsLoading || programsLoading) {
    return <LoadingSpinner />;
  }

  // Get the program version info from the first inspection program
  const inspectionProgram = inspectionPrograms?.[0];
  const versionId = inspectionProgram?.programVersionId;
  const programId = inspectionProgram?.programId;

  if (!versionId || !programId) {
    return (
      <Container>
        <Alert severity="error" className={styles.error}>
          No program version information found for this inspection
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>Administra tus Hallazgos</h1>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCSV}
              className={styles.downloadButton}
            >
              Download CSV
            </Button>
          </div>
          {undoStack.length > 0 && (
            <button onClick={handleUndo} className={styles.undoButton}>
              Undo Last Action
            </button>
          )}
        </div>
        
        <div className={styles.content}>
          {findings?.map((finding: InspectionsFindingDto) => (
            <Finding
              key={finding.id}
              finding={finding}
              onUpdate={handleUpdate}
              onAddFindingItem={handleAddFindingItem}
              onRemoveFindingItem={handleRemoveFindingItem}
              organizationId={selectedOrganization?.id || ''}
              programId={programId}
              versionId={versionId}
            />
          ))}
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Container>
  );
};

export default InspectionFindingsPage; 